import * as queryString from 'querystring';

import { getConfig } from '../config';
import { store } from '../index';
import { Tokens } from '../store/keycloak';
import { AppState } from '../store/store';
import { checkErrorsAndNotices } from '../utils';

export enum ApiMethodType {
    GET = 'GET',
    POST = 'POST',
    POST_MULTIPART = 'POST_MULTIPART',
    PUT = 'PUT',
    DELETE = 'DELETE',
    PATCH = 'PATCH',
}

//  eslint-disable-next-line @typescript-eslint/no-explicit-any
type requestPayload = Record<string, any>;

type requestOptions = Record<string, HeadersInit | keyof typeof ApiMethodType | unknown> & {
    headers?: HeadersInit;
    method: keyof typeof ApiMethodType;
};

class ApiClient {
    public async request<T>(
        path: string,
        options: requestOptions = { method: ApiMethodType.GET },
        payload?: requestPayload,
        controller?: AbortController
    ): Promise<T> {
        const {
            intl: { locale },
            keycloak,
        } = store.getState() as AppState;
        const tokens = keycloak.tokens as Tokens;
        let keycloakToken: string | null = null;
        let keycloakRefreshToken: string | null = null;

        if (tokens && tokens.token && tokens.refreshToken) {
            keycloakToken = tokens.token as string;
            keycloakRefreshToken = tokens.refreshToken as string;
        }

        const apiToken = keycloakToken || window.sessionStorage.getItem('bearerTokenEC');
        const refreshToken = keycloakRefreshToken || window.sessionStorage.getItem('refreshTokenEC');
        const isMultiPart = options.method === ApiMethodType.POST_MULTIPART;
        const isGet = options.method === ApiMethodType.GET;
        const query = isGet && !!payload ? `?${queryString.stringify(payload)}` : '';

        const init: RequestInit = {
            method: isMultiPart ? ApiMethodType.POST : options.method,
            headers: {
                'Content-Type': isMultiPart ? 'multipart/form-data' : 'application/json',
                Accept: 'application/json',
                'accept-language-master': locale || 'de',
                ...(apiToken && { Authorization: `Bearer ${apiToken}` }),
                ...(refreshToken && { 'Authorization-Refresh': refreshToken }),
                ...options.headers,
            },
            body: !isGet && !!payload ? ((isMultiPart ? payload : JSON.stringify(payload)) as BodyInit) : null,
            signal: controller?.signal,
        };

        if (isMultiPart && init && init.headers) {
            delete init.headers['Content-Type'];
        }

        const request = await fetch(`${getConfig('apiUrl')}${path}${query}`, init);
        const setToken = request.headers.get('set-token');
        const setRefreshToken = request.headers.get('set-refreshtoken');

        if (setToken) {
            window.sessionStorage.setItem('bearerTokenEC', setToken);
        }

        if (setRefreshToken) {
            window.sessionStorage.setItem('refreshTokenEC', setRefreshToken);
        }

        if (!request.ok) {
            checkErrorsAndNotices(store, await request.json());

            throw {
                code: request.status,
                message: request.statusText,
            };
        }

        try {
            const res = (await request.json()) as T;
            checkErrorsAndNotices(store, res);

            return res;
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return {} as T;
        }
    }
}

export default new ApiClient();
